import type { BatchListRow } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import type { Column } from '../../atoms/DataTable.tsx'

/**
 * 「생성 · 시작 · 종료」 시각 열 — 작업 목록과 이력이 **같은 함수**를 쓴다(두 표가 같은 값을 다르게 말하지 않게 ·
 * eric 2026-09-30 「작업 이력 리스트에서.. 작업 생성시간 종료시간 완료시간은 리스트에 포함시켜줘」).
 *
 * 값은 전부 `toBatchListRow()`(`routes/batchModel.ts`)가 `dateTimeText()` 로 만든 표기다 — 여기서 다시 포맷하지 않는다.
 * 열 `key`(`created` · `started` · `ended`)는 `listColumnPriority.ts` · `batchListedFields.ts` 와 짝이다.
 */

/**
 * 생성 — `Batch.created_at`. 🔴 **응답에 늘 있는 값이라 자리를 만든다.** 시작 전 배치는 「시작」이
 * `—` 뿐이라, 이 열이 없으면 목록에서 언제 만든 작업인지 알 길이 없었다.
 */
export function batchCreatedColumn(): Column<BatchListRow> {
  return {
    key: 'created',
    header: t('column.createdAt'),
    nowrap: true,
    // 시각은 내용 폭만 쓴다 — 남는 폭은 채우는 열로 간다(`Column.width`)
    width: 'fit',
    // 🔴 원값(ISO)으로 세운다 — 표기는 연도가 없다(`BatchListRow.created_at`)
    sort: 'time',
    sortValue: (row) => row.created_at,
    cell: (row) => <span className="muted mono num">{row.created_text}</span>,
  }
}

/** 시작 — `Batch.started_at`. 시작 안 한 채 끝난(중지된) 작업은 `—` 다 */
export function batchStartedColumn(): Column<BatchListRow> {
  return {
    key: 'started',
    header: t('column.startedAt'),
    nowrap: true,
    // 시각은 내용 폭만 쓴다 — 남는 폭은 채우는 열로 간다(`Column.width`)
    width: 'fit',
    sort: 'time',
    sortValue: (row) => row.started_at,
    cell: (row) => <span className="muted mono num">{row.started_text}</span>,
  }
}

/**
 * 종료 — `Batch.completed_at`. 🔴 **머리글은 「완료」가 아니라 「종료」다.** 서버는 이 칸을 종착 전이마다 쓴다 —
 * `aborted`(중지) 도 채워진다(2026-09-30 실측 aborted 100/100 · 시작 안 한 채 중지된 행도). 「완료」라 적으면 중지된
 * 작업이 완료 시각을 가진 것처럼 읽힌다. 작업 상세 「작업」 카드는 「작업 종료」다(2026-10-22 eric — 같은 값 · 카드 전용 이름). ⚠ 시료 보고서는 여전히 `column.completedAt`(완료)를 쓴다.
 * 🔴 **진행 중 목록에도 선다**(eric 2026-09-30 「진행 중 탭 컬럼도.. 이력 컬럼에서 소요 시간 빼고 동일하게」) — 종착 전이면
 * `—` 이고, 종착 직후(목록이 다시 받기 전) 잠깐 값이 선다. 종전 「작업 목록에는 이 열을 두지 않는다」는 뒤집혔다.
 */
export function batchEndedColumn(): Column<BatchListRow> {
  return {
    key: 'ended',
    header: t('column.endedAt'),
    nowrap: true,
    // 시각은 내용 폭만 쓴다 — 남는 폭은 채우는 열로 간다(`Column.width`)
    width: 'fit',
    sort: 'time',
    sortValue: (row) => row.completed_at,
    cell: (row) => <span className="muted mono num">{row.completed_text}</span>,
  }
}
